import { Select } from './Select';
import React from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface Column<T> {
  key: string;
  header: React.ReactNode;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (value: any, row: T, index: number) => React.ReactNode;
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor: (row: T, index: number) => string | number;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string) => void;
  onRowClick?: (row: T) => void;
  /**
   * Кнопка «Открыть» в первой ячейке кликабельной строки (доступ с клавиатуры). Включена внутри
   * `TableRowActionsProvider` (разделы аналитики) или явной подписью; false — у страницы своя ссылка/кнопка.
   */
  rowActionLabel?: string | ((row: T) => string) | false;
  loading?: boolean;
  emptyMessage?: string;
  className?: string;
  /** Compact density (smaller padding) */
  compact?: boolean;
  /** Sticky header (default: true) */
  stickyHeader?: boolean;
  /**
   * Horizontal scrolling by the table's own wrapper (default: true).
   * Turn it off when the page already scrolls the list: any `overflow` on the
   * wrapper makes it a scrollport, and `stickyHeader` then anchors to that
   * wrapper instead of the page container — so the header scrolls out of view.
   */
  scrollX?: boolean;
  /**
   * Наведение на строку (`null` — указатель ушёл с таблицы): связь строки с графиком — строка под мышью
   * подсвечивает свой ряд/столбец.
   */
  onRowHover?: (row: T | null, index: number) => void;
  /** Дополнительный класс строки: подсветка строки, выбранной на графике. */
  rowClassName?: (row: T, index: number) => string | undefined | null | false;
}

const TableRowActionsContext = React.createContext(false);

/**
 * Включает кнопку «Открыть» у кликабельных строк всех таблиц внутри. Подключена только к разделам
 * аналитики: остальной PIM строит на `onRowClick` каталоги и журналы, где лишняя ссылка под
 * чекбоксом или превью ломает вёрстку.
 */
export function TableRowActionsProvider({ children }: { children: React.ReactNode }) {
  return <TableRowActionsContext.Provider value>{children}</TableRowActionsContext.Provider>;
}

export function Table<T>({
  columns,
  data,
  keyExtractor,
  sortColumn,
  sortDirection,
  onSort,
  onRowClick,
  rowActionLabel,
  loading,
  emptyMessage = 'Нет данных',
  className,
  compact = false,
  stickyHeader = true,
  scrollX = true,
  onRowHover,
  rowClassName,
}: TableProps<T>) {
  const rowActionsEnabled = React.useContext(TableRowActionsContext);
  const cellPadding = compact ? 'px-3 py-2' : 'px-5 py-3';
  const headerPadding = compact ? 'px-3 py-2' : 'px-5 py-2.5';

  return (
    <div className={cn(scrollX && 'overflow-x-auto', className)}>
      {/* `w-full` (not `min-w-full`) so columns distribute available width.
          With `min-w-full` the table only stretched if column-sum exceeded 100% — left
          a gap on the right when columns had compact intrinsic widths. */}
      <table className="w-full border-separate border-spacing-0">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={cn(
                  headerPadding,
                  'bg-gray-50 border-b border-gray-200 dark:bg-zinc-900 dark:border-zinc-800',
                  'text-2xs font-semibold uppercase tracking-wider text-gray-500 dark:text-zinc-400',
                  'text-left',
                  stickyHeader && 'sticky top-0 z-10',
                  column.sortable && 'cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors',
                  column.align === 'center' && 'text-center',
                  column.align === 'right' && 'text-right'
                )}
                style={{ width: column.width }}
                aria-sort={
                  column.sortable && sortColumn === column.key
                    ? sortDirection === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : undefined
                }
              >
                {column.sortable ? (
                  <button
                    type="button"
                    className={cn(
                      'flex w-full items-center gap-1 rounded-sm focus:outline-none focus:ring-2 focus:ring-indigo-500',
                      column.align === 'center' && 'justify-center',
                      column.align === 'right' && 'justify-end',
                    )}
                    onClick={() => onSort?.(column.key)}
                  >
                    {column.header}
                    {sortColumn === column.key && (
                      sortDirection === 'asc'
                        ? <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
                        : <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                  </button>
                ) : (
                  <div
                    className={cn(
                      'flex items-center gap-1',
                      column.align === 'center' && 'justify-center',
                      column.align === 'right' && 'justify-end',
                    )}
                  >
                    {column.header}
                  </div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={columns.length} className="text-center py-12">
                <div className="flex flex-col items-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400" />
                  <span className="mt-2 text-sm text-gray-500 dark:text-zinc-400">Загрузка...</span>
                </div>
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="text-center py-12">
                <span className="text-sm text-gray-500 dark:text-zinc-400">{emptyMessage}</span>
              </td>
            </tr>
          ) : (
            data.map((row, index) => (
              <tr
                key={keyExtractor(row, index)}
                className={cn(
                  'transition-colors hover:bg-gray-50 dark:hover:bg-zinc-800/60',
                  onRowClick && 'cursor-pointer',
                  rowClassName?.(row, index)
                )}
                onMouseOver={onRowHover ? () => onRowHover(row, index) : undefined}
                onMouseLeave={onRowHover ? () => onRowHover(null, -1) : undefined}
                onClick={(event) => {
                  const target = event.target as HTMLElement;
                  if (target.closest('button, input, select, textarea, a, label, [role="button"], [role="checkbox"], [role="combobox"]')) {
                    return;
                  }
                  onRowClick?.(row);
                }}
              >
                {columns.map((column, columnIndex) => {
                  const value = (row as any)[column.key];
                  const content = column.render ? column.render(value, row, index) : value;
                  const rowAction = onRowClick && rowActionLabel !== false && (rowActionsEnabled || rowActionLabel !== undefined) && columnIndex === 0;
                  const plain = typeof content === 'string' || typeof content === 'number';
                  const actionLabel = typeof rowActionLabel === 'function' ? rowActionLabel(row) : rowActionLabel || (plain ? `Открыть: ${content}` : 'Открыть');
                  return (
                    <td
                      key={column.key}
                      className={cn(
                        cellPadding,
                        'text-sm text-gray-900 dark:text-zinc-100 align-middle border-b border-gray-100 dark:border-zinc-800',
                        column.align === 'center' && 'text-center',
                        column.align === 'right' && 'text-right'
                      )}
                    >
                      {rowAction ? (
                        <>
                          {!plain && content}
                          <button
                            type="button"
                            aria-label={actionLabel}
                            onClick={() => onRowClick(row)}
                            className={cn('rounded-sm text-left text-indigo-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-indigo-300', !plain && 'mt-1 block text-xs')}
                          >
                            {plain ? content : 'Открыть'}
                          </button>
                        </>
                      ) : content}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// Pagination component
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
}) => {
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  const pgBtn =
    'inline-flex items-center justify-center min-w-[32px] h-8 px-2 ' +
    'text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md ' +
    'hover:bg-gray-50 hover:border-gray-400 ' +
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-300 ' +
    'dark:text-zinc-200 dark:bg-zinc-800 dark:border-zinc-700 dark:hover:bg-zinc-700 dark:hover:border-zinc-600 ' +
    'dark:disabled:hover:bg-zinc-800 dark:disabled:hover:border-zinc-700 ' +
    'transition-colors';

  return (
    <div className="flex items-center justify-between gap-4 px-5 py-3 border-t border-gray-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
      <div className="flex items-center gap-4">
        <span className="text-xs text-gray-500 dark:text-zinc-400">
          Показаны <span className="font-medium text-gray-900 dark:text-zinc-100">{startItem}–{endItem}</span> из <span className="font-medium text-gray-900 dark:text-zinc-100">{totalItems}</span>
        </span>
        {onPageSizeChange && (
          <Select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="h-8 px-2 text-xs border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-200"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size} / стр.
              </option>
            ))}
          </Select>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button onClick={() => onPageChange(1)} disabled={currentPage === 1} className={pgBtn} aria-label="Первая страница">«</button>
        <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className={pgBtn} aria-label="Предыдущая">‹</button>
        <span className="px-3 text-xs text-gray-700 dark:text-zinc-300">
          Страница <span className="font-medium text-gray-900 dark:text-zinc-100">{currentPage}</span> из {totalPages}
        </span>
        <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} className={pgBtn} aria-label="Следующая">›</button>
        <button onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages} className={pgBtn} aria-label="Последняя">»</button>
      </div>
    </div>
  );
};

export default Table;
